import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Tag, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * Controllo sconto riutilizzabile, da usare nel CARRELLO/ordine (così lo sconto
 * viene salvato sull'ordine e appare anche sul resoconto NON fiscale, non solo al
 * pagamento). Rispetta i due switch: "Codici sconto" e "Sconti prefatti".
 *
 * Props:
 *  - order: ordine corrente (serve order.id)
 *  - onApplied(updatedOrder): callback con l'ordine aggiornato (total ricalcolato)
 */
export const DiscountControl = ({ order, onApplied, className }) => {
  const { t } = useTranslation();
  // Sconti attivi di default: il campo appare finché non vengono disattivati
  // esplicitamente da Impostazioni (evita che resti nascosto se mai configurato).
  const [settings, setSettings] = useState({ codes_enabled: true, presets_enabled: true });
  const [presets, setPresets] = useState([]);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const fmtEur = (n) => `\u20ac${Number(n || 0).toFixed(2)}`;

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [st, ds] = await Promise.all([api.get('/discounts/settings'), api.get('/discounts')]);
        if (!active) return;
        setSettings(st.data || { codes_enabled: true, presets_enabled: true });
        setPresets((ds.data || []).filter((d) => d.active !== false));
      } catch (e) {
        // In caso di errore lascia il controllo visibile (resiliente).
        if (active) { setSettings({ codes_enabled: true, presets_enabled: true }); setPresets([]); }
      }
    })();
    return () => { active = false; };
  }, []);

  if (!order?.id) return null;
  if (!settings.codes_enabled && !settings.presets_enabled) return null;

  const hasDiscount = Number(order.discount_amount || 0) > 0;
  const presetLabel = (d) => (d.type === 'percent' ? `${Number(d.value)}%` : fmtEur(d.value));

  const apply = async (c) => {
    if (!c) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/discount/apply`, { code: c });
      onApplied?.(data);
      setCode('');
      toast.success(t('payment.discountApplied', { amount: fmtEur(data.discount_amount) }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('payment.invalidCode'));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/discount/remove`);
      onApplied?.(data);
      toast.success(t('payment.discountRemoved'));
    } catch (e) { toast.error(t('common.error')); }
    finally { setBusy(false); }
  };

  return (
    <div className={className} data-testid="cart-discount-control">
      <div className="mb-1.5 flex items-center gap-1.5 text-sm text-muted-foreground">
        <Tag className="h-4 w-4 text-primary" /> {t('payment.discountLabel')}
      </div>
      {hasDiscount ? (
        <div className="flex items-center justify-between gap-2">
          <span className="font-mono font-semibold text-primary">{order.discount_code} · -{fmtEur(order.discount_amount)}</span>
          <Button variant="ghost" size="sm" className="rounded-lg text-destructive" onClick={remove} disabled={busy} data-testid="cart-discount-remove">
            <X className="h-4 w-4 mr-1" /> {t('common.remove')}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {settings.presets_enabled && presets.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="cart-discount-presets">
              {presets.map((d) => (
                <Button key={d.id} type="button" variant="outline" size="sm" className="rounded-full" disabled={busy} onClick={() => apply(d.code)} data-testid={`cart-discount-preset-${d.id}`}>
                  {d.code} · {presetLabel(d)}
                </Button>
              ))}
            </div>
          )}
          {settings.codes_enabled && (
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t('payment.discountCode')} className="font-mono uppercase h-10" data-testid="cart-discount-input" />
              <Button variant="outline" className="rounded-xl shrink-0 h-10" onClick={() => apply(code)} disabled={busy || !code} data-testid="cart-discount-apply">{t('payment.apply')}</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
